import { LogLevel } from '@signage-player/contracts';
import { AbstractLogger, isSameOrHigherLevel } from '@signage-player/logger';
import { posthog } from 'posthog-js';
/**
 * PostHog logger using posthog-js (browser SDK).
 * Mirrors logger-node/PostHogLogger.ts. Differences: uses posthog-js singleton
 * instead of `new PostHog(...)`, autocapture + exception capture disabled.
 */
export class PostHogLogger extends AbstractLogger {
    constructor(minimumLevel, config) {
        super(minimumLevel);
        this.config = config;
        this.initialized = false;
        if (this.config.logging.posthog?.apiKey && this.config.logging.posthog?.host) {
            posthog.init(this.config.logging.posthog.apiKey, {
                api_host: this.config.logging.posthog.host,
                autocapture: false,
                capture_pageview: false,
                capture_exceptions: false,
                disable_session_recording: true,
            });
            this.initialized = true;
        }
    }
    setAdditionalContext(context) {
        super.setAdditionalContext(context);
        if (!this.initialized)
            return;
        const additionalContext = this.getAdditionalContext();
        // No localIp fallback: web localIp is the base-class '127.0.0.1' constant,
        // which would merge every device into one PostHog person.
        const distinctId = additionalContext.user?.id || additionalContext.device?.device_unique_identifier;
        if (distinctId) {
            posthog.identify(distinctId, {
                ...(additionalContext.os?.name ? { $os: additionalContext.os.name } : {}),
                ...(additionalContext.os?.version ? { os_version: additionalContext.os.version } : {}),
                ...(additionalContext.device?.device_unique_identifier
                    ? { device_hash: additionalContext.device.device_unique_identifier } : {}),
                ...(additionalContext.user?.ip_address ? { ip_address: additionalContext.user.ip_address } : {}),
                app_version: this.config.version,
            });
        }
    }
    log(level, message, context) {
        if (!this.initialized)
            return;
        if (!isSameOrHigherLevel(level, this.minimumLevel))
            return;
        let severityLevel = 'log';
        switch (level) {
            case LogLevel.EMERGENCY:
            case LogLevel.ALERT:
            case LogLevel.CRITICAL:
                severityLevel = 'fatal';
                break;
            case LogLevel.ERROR:
                severityLevel = 'error';
                break;
            case LogLevel.WARNING:
                severityLevel = 'warning';
                break;
            case LogLevel.NOTICE:
            case LogLevel.INFO:
                severityLevel = 'info';
                break;
            case LogLevel.DEBUG:
                severityLevel = 'debug';
                break;
        }
        const additionalContext = this.getAdditionalContext();
        posthog.captureException(`[${level}] ${message}`, {
            level: severityLevel,
            $os: additionalContext.os?.name,
            $ip: additionalContext.user?.ip_address,
            device_hash: additionalContext.device?.device_unique_identifier,
            $set: {
                ...(additionalContext.os?.name ? { $os: additionalContext.os.name } : {}),
                ...(additionalContext.os?.version ? { os_version: additionalContext.os.version } : {}),
                ...(additionalContext.device?.device_unique_identifier
                    ? { device_hash: additionalContext.device.device_unique_identifier } : {}),
                ...(additionalContext.user?.ip_address ? { ip_address: additionalContext.user.ip_address } : {}),
                app_version: this.config.version,
            },
            contexts: {
                ...additionalContext,
                extras: { ...this.clearContext(context) },
                app: {
                    app_identifier: this.config.appIdentifier,
                    app_name: this.config.appName,
                    app_version: this.config.version,
                    app_start_time: this.config.startTime.toISOString(),
                },
            },
            tags: { environment: this.config.environment },
        });
    }
    async destroy(_timeout) {
        // posthog-js has no shutdown API; it flushes via sendBeacon on pagehide.
        // Never posthog.reset() here — that's the logout API and would wipe the
        // device's distinct_id on every teardown.
    }
    async flush() {
        // posthog-js flushes asynchronously; no manual flush API. No-op.
    }
    setMinimumLevel(level) {
        this.minimumLevel = level;
    }
    getMinimumLevel() {
        return this.minimumLevel;
    }
}
export default PostHogLogger;
//# sourceMappingURL=PostHogLogger.js.map